/**
 * The Ledger page picker's option list (#1944, DESIGN_SYSTEM.md 8.15), shared
 * by the add row, the edit row, the card editor and the quick-add Sheet.
 *
 * Ledger-linked payees come first as their own group: every one that matches
 * the typed prefix, outside the cap, with archived ledgers' payees last and
 * this ledger's own payee left out (an entry never transfers to its own
 * ledger). The regular payees follow, name-ordered as loaded and capped, and
 * never repeat a linked one.
 *
 * An income provider (#2288) appears twice among the regular payees: itself,
 * then its Paycheck option directly under it. The cap counts payees, not
 * options. A Credit Card ledger takes no Income (#2289), so `incomeAllowed`
 * false lists the provider once.
 */

import { paycheckOptionOf } from './entryType';

export const PAYEE_OPTION_CAP = 15;

/**
 * @param {Array} payees                  the workspace's payees, name-sorted
 * @param {string} input                  the payee field's text
 * @param {object} context
 * @param {string} context.ledgerId       the ledger being entered on
 * @param {Set<string>} [context.archivedLedgerIds]
 * @param {boolean} [context.incomeAllowed]  false on a Credit Card ledger
 * @returns {Array} options, linked group first
 */
export const payeeOptions = (payees, input, { ledgerId, archivedLedgerIds = new Set(), incomeAllowed = true }) => {
  const prefix = (input || '').trim().toLowerCase();
  const matches = (p) => !prefix || (p.name || '').toLowerCase().startsWith(prefix);
  const archived = (p) => (archivedLedgerIds.has(p.linkedLedgerId) ? 1 : 0);

  const linked = payees
    .filter((p) => p.linkedLedgerId && p.linkedLedgerId !== ledgerId && matches(p))
    .sort((a, b) => archived(a) - archived(b) || (a.name || '').localeCompare(b.name || ''));
  const regular = payees
    .filter((p) => !p.linkedLedgerId && matches(p))
    .slice(0, PAYEE_OPTION_CAP)
    .flatMap((p) => (p.incomeProvider && incomeAllowed ? [p, paycheckOptionOf(p)] : [p]));
  return [...linked, ...regular];
};
